import { Slot, SlotFillProvider, withFilters }                from '@wordpress/components';
import { __ }                                                 from '@wordpress/i18n';
import { Formik, Form }                                       from 'formik';
import React                                                  from 'react';
import RadioGroupFieldOptionInterface                         from '../../../interfaces/RadioGroupFieldOptionInterface';
import SettingsFormContainerPropsInterface                    from '../../../interfaces/SettingsFormContainerPropsInterface';
import SettingsFormPropsPlanInterface                         from '../../../interfaces/SettingsFormPropsPlanInterface';
import AdminPageSection                                       from '../AdminPageSection';
import RadioGroupField                                        from '../Fields/RadioGroupField';
import TextField                                              from '../Fields/TextField';
import TrustBadgeRepeater                                     from '../TrustBadgeRepeater';
import ToggleCheckboxField                                    from '../Fields/ToggleCheckboxField';
import SelectField                                            from '../Fields/SelectField';
import NumberField                                            from '../Fields/NumberField';
import LockedFieldWrapper                                     from '../LockedFieldWrapper';

const radioOptions: RadioGroupFieldOptionInterface[] = [
    {
        value: 'below_cart_summary',
        label: __( 'Below the checkout cart summary', 'checkout-wc' ),
        description: __( 'Output in a single column below the checkout cart summary totals', 'checkout-wc' ),
    },
    {
        value: 'below_checkout_form',
        label: __( 'After the checkout form', 'checkout-wc' ),
        description: __( 'Output in a single row below the checkout form above the footer', 'checkout-wc' ),
    },
    {
        value: 'in_footer',
        label: __( 'Top of the footer', 'checkout-wc' ),
        description: __( 'Output in a single row inside the footer', 'checkout-wc' ),
    },
];

interface TrustBadgeSettingsInterface {
    enable_trust_badges: boolean;
    trust_badge_position: string;
    trust_badges_title: string;
    trust_badges: any[];
    enable_wc_review_badges: boolean;
    wc_review_source: string;
    wc_review_min_rating: string;
    wc_review_limit: number;
}

interface TrustBadgeFormWooCommerceSettingsInterface {
    shipping_methods: any;
}
interface TrustBadgeSettingsFormPropsInterface extends SettingsFormContainerPropsInterface {
    settings: TrustBadgeSettingsInterface;
    woocommerce_settings: TrustBadgeFormWooCommerceSettingsInterface;
    plan: SettingsFormPropsPlanInterface;
    params: any;

}

const TrustBadgeSettingsForm: React.FC<TrustBadgeSettingsFormPropsInterface> = ( props ) => {
    const { saveSettings, isLoading, searchTerm } = props;

    const AdditionalSettings = withFilters(
        'CheckoutWC.Admin.Settings.Pages.TrustBadge',
    )( () => <></> );

    return (
        <SlotFillProvider>
            <AdditionalSettings />
            <Formik
                initialValues={{
                    ...props.settings,
                }}
                enableReinitialize={true}
                onSubmit={async ( values ) => {
                    await saveSettings( values );
                }}
            >
                {( { values } ) => (
                    <Form className={'space-y-6 transition-all'} style={{ filter: isLoading ? 'blur(2px)' : 'none' }}>
                        <AdminPageSection
                            title={__( 'Trust Badge Options', 'checkout-wc' )}
                            description={__( 'Configure general Trust Badge options.', 'checkout-wc' )}
                            content={
                                <>
                                    <ToggleCheckboxField
                                        name='enable_trust_badges'
                                        label={__( 'Enable Trust Badges', 'checkout-wc' )}
                                        description={__( 'Enable trust badges on CheckoutWC templates. Uncheck to hide badges.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                        disabled={props.plan.plan_level < 1}
                                    />

                                    { props.plan.plan_level >= 1 && values.enable_trust_badges
                                        && (
                                            <>
                                                <RadioGroupField
                                                    name='trust_badge_position'
                                                    options={radioOptions}
                                                    label={__( 'Trust Badge Output Location', 'checkout-wc' )}
                                                    description={__( 'Where to display the trust badges on the checkout page.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />
                                                <TextField
                                                    name="trust_badges_title"
                                                    label={__( 'Heading', 'checkout-wc' )}
                                                    description={__( 'Example: Why choose us?', 'checkout-wc' )}
                                                    placeholder={__( 'Example: Why choose us?', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />

                                                <Slot name="CheckoutWC.Admin.Pages.TrustBageOptions" />
                                            </>
                                        )
                                    }
                                </>
                            }
                        />

                        { props.plan.plan_level >= 1 && values.enable_trust_badges
                            && (
                                <AdminPageSection
                                    title={__( 'WooCommerce Reviews', 'checkout-wc' )}
                                    description={__( 'Automatically display WooCommerce product reviews as trust badges.', 'checkout-wc' )}
                                    content={
                                        <LockedFieldWrapper plan={props.plan} slug={'trust-badges-wc-reviews'} locked={props.plan.plan_level < 3} requiredPlans={props.plan.labels.required_list[ 3 ]}>
                                            <ToggleCheckboxField
                                                name='enable_wc_review_badges'
                                                label={__( 'Enable WooCommerce Review Badges', 'checkout-wc' )}
                                                description={__( 'Automatically show product reviews as trust badges alongside manually created badges.', 'checkout-wc' )}
                                                searchTerm={searchTerm}
                                                disabled={props.plan.plan_level < 3}
                                            />

                                            { props.plan.plan_level >= 3 && values.enable_wc_review_badges
                                                && (
                                                    <>
                                                        <SelectField
                                                            name='wc_review_source'
                                                            label={__( 'Review Source Priority', 'checkout-wc' )}
                                                            description={__( 'Choose which reviews to prioritize for display.', 'checkout-wc' )}
                                                            options={[
                                                                { value: 'cart_only', label: __( 'Cart products only', 'checkout-wc' ) },
                                                                { value: 'cart_first', label: __( 'Cart products first, then site-wide', 'checkout-wc' ) },
                                                                { value: 'sitewide', label: __( 'Any product reviews', 'checkout-wc' ) }
                                                            ]}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <SelectField
                                                            name='wc_review_min_rating'
                                                            label={__( 'Minimum Star Rating', 'checkout-wc' )}
                                                            description={__( 'Only show reviews with this rating or higher.', 'checkout-wc' )}
                                                            options={[
                                                                { value: '3', label: __( '3+ stars', 'checkout-wc' ) },
                                                                { value: '4', label: __( '4+ stars', 'checkout-wc' ) },
                                                                { value: '5', label: __( '5 stars only', 'checkout-wc' ) }
                                                            ]}
                                                            searchTerm={searchTerm}
                                                        />

                                                        <NumberField
                                                            name='wc_review_limit'
                                                            label={__( 'Maximum Reviews to Show', 'checkout-wc' )}
                                                            description={__( 'How many WooCommerce reviews to display (1-5)', 'checkout-wc' )}
                                                            min={1}
                                                            max={5}
                                                            searchTerm={searchTerm}
                                                        />
                                                    </>
                                                )
                                            }
                                        </LockedFieldWrapper>
                                    }
                                />
                            )
                        }

                        { props.plan.plan_level >= 1 && values.enable_trust_badges
                            && (
                                <AdminPageSection
                                    title={__( 'Trust Badges', 'checkout-wc' )}
                                    description={__( 'Create trust badges to display on your checkout page.', 'checkout-wc' )}
                                    content={<TrustBadgeRepeater name={'trust_badges'}/>}
                                />
                            )
                        }

                        <button className={'cfw_admin_page_submit hidden'} type="submit">Submit</button>
                    </Form>
                )}
            </Formik>
        </SlotFillProvider>
    );
};

export default TrustBadgeSettingsForm;
